import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import API_BASE from '../../utils/apiBase';

/**
 * Small, non-blocking pre-engagement document card.
 * Applicant progress/recruitment approval and staff readiness are NEVER
 * derived from a KID card. The backend remains the only evidence source.
 */
export default function ApplicantKidPanel({ employeeId, isAuditor = false }) {
  const { token } = useAuth();
  const [state, setState] = useState(null);
  const [pending, setPending] = useState(false);
  const [issue, setIssue] = useState('');
  const refresh = useCallback(async () => {
    if (!employeeId || !token) return;
    try {
      const res = await axios.get(
        `${API_BASE}/employees/${employeeId}/kid/readiness`,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 18000 },
      );
      setState(res.data);
      setIssue('');
    } catch (err) {
      setIssue(err.response?.data?.detail?.message || 'KID status unavailable. Try Refresh.');
    }
  }, [employeeId, token]);

  useEffect(() => { refresh(); }, [refresh]);

  const prepare = async () => {
    if (!employeeId || isAuditor || pending) return;
    setPending(true);
    setIssue('');
    try {
      await axios.post(
        `${API_BASE}/employees/${employeeId}/kid/prepare`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 35000 },
      );
      await refresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setIssue(typeof detail === 'string' ? detail : detail?.message || 'Could not prepare the KID.');
    } finally { setPending(false); }
  };

  const kid = state?.kid || {};
  const blockers = state?.readiness?.blockers || [];
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4" data-testid="pre-engagement-kid-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">PAYE Key Information Document</h3>
          <p className="mt-1 text-xs text-slate-600">
            Provide to PAYE agency applicants before agreeing terms. This does not approve recruitment or sign a contract.
          </p>
        </div>
        <span className={`rounded-full px-2 py-1 text-xs font-medium ${kid.verified ? 'bg-green-100 text-green-800' : kid.rendered_file_url ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'}`}>
          {kid.status === 'outdated' ? 'Reissue required' : kid.verified ? 'Receipt acknowledged' : kid.rendered_file_url ? 'Prepared — awaiting worker' : 'Not prepared'}
        </span>
      </div>
      {issue && <p role="alert" className="mt-3 text-sm text-red-700">{issue}</p>}
      {!state?.readiness?.ready && blockers.length > 0 && (
        <div className="mt-3 rounded-lg bg-amber-50 p-3 text-xs text-amber-900">
          {blockers.map((b, i) => <p key={i}>{b.fix_hint || b.label}</p>)}
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        {kid.rendered_file_url && (
          <a href={kid.rendered_file_url} target="_blank" rel="noopener noreferrer"
            className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-medium text-slate-800">
            View prepared PDF
          </a>
        )}
        {!isAuditor && (
          <button type="button" onClick={prepare} disabled={pending || !state?.readiness?.ready}
            className="rounded-lg bg-teal-700 px-3 py-2 text-xs font-medium text-white disabled:opacity-50">
            {pending ? 'Preparing…' : kid.rendered_file_url ? 'Check / refresh KID' : 'Prepare PAYE KID'}
          </button>
        )}
        <button type="button" onClick={refresh}
          className="rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-700">
          Refresh
        </button>
      </div>
      {kid.rendered_file_url && !kid.verified && (
        <p className="mt-2 text-xs text-slate-600">
          The applicant can view and acknowledge receipt in their portal. Prepared does not mean read, received, agreed or approved.
        </p>
      )}
    </section>
  );
}
